import {
  ApiBlueprint,
  bitbucketAuthApiRef,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  githubAuthApiRef,
  gitlabAuthApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  oauthRequestApiRef,
  storageApiRef,
} from '@backstage/frontend-plugin-api';
import { AnalyticsImplementationBlueprint } from '@backstage/plugin-app-react';
import { GithubAuth, MicrosoftAuth, OAuth2 } from '@backstage/core-app-api';
import { ScmAuth, scmAuthApiRef } from '@backstage/integration-react';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import { MatomoAnalytics } from '../analytics/MatomoAnalytics';
import {
  cloudIdpAuthApiRef,
  githubEMUAuthApiRef,
  githubEnterpriseAuthApiRef,
  microsoftChinaAuthApiRef,
} from '../apiRefs';
import { StarIcon } from '@xlrt/components';

// Utility APIs of the app. Extensions named like a core API extension of the
// `app` plugin (`scm-auth`, `storage`) replace that one; the others add APIs
// of this app. APIs that a plugin already provides (catalog import,
// notifications, scm integrations) are not repeated here.

export const cloudIdpAuthApi = ApiBlueprint.make({
  name: 'cloudidp-auth',
  params: defineParams =>
    defineParams({
      api: cloudIdpAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'cloudidp-provider',
            title: 'CARIAD CloudIDP Provider',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
          popupOptions: {
            // optional, used to customize login in popup size
            size: {
              height: 600,
              width: 600,
            },
          },
        }),
    }),
});

export const microsoftChinaAuthApi = ApiBlueprint.make({
  name: 'microsoft-china-auth',
  params: defineParams =>
    defineParams({
      api: microsoftChinaAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        MicrosoftAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'microsoft-china-provider',
            title: 'Microsoft China Provider',
            icon: StarIcon,
          },
          environment: configApi.getOptionalString('auth.environment'),
        }),
    }),
});

export const githubEnterpriseAuthApi = ApiBlueprint.make({
  name: 'github-enterprise-auth',
  params: defineParams =>
    defineParams({
      api: githubEnterpriseAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        GithubAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'github-enterprise',
            title: 'GitHub Enterprise',
            icon: StarIcon,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['read:user', 'repo'],
        }),
    }),
});

export const githubEmuAuthApi = ApiBlueprint.make({
  name: 'github-enterprise-emu-auth',
  params: defineParams =>
    defineParams({
      api: githubEMUAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        GithubAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'github-enterprise-emu',
            title: 'GitHub Enterprise EMU',
            icon: StarIcon,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['read:user', 'repo'],
        }),
    }),
});

// Replaces `api:app/scm-auth`: the default providers plus GitHub Enterprise
// Server and GitHub Enterprise Cloud EU (EMU).
export const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: {
        githubAuthApi: githubAuthApiRef,
        githubEnterpriseAuthApi: githubEnterpriseAuthApiRef,
        githubEMUAuthApi: githubEMUAuthApiRef,
        gitlabAuthApi: gitlabAuthApiRef,
        microsoftAuthApi: microsoftAuthApiRef,
        bitbucketAuthApi: bitbucketAuthApiRef,
      },
      factory: apis =>
        ScmAuth.merge(
          // Default providers (same as ScmAuth.createDefaultApiFactory)
          ScmAuth.forGithub(apis.githubAuthApi),
          ScmAuth.forGitlab(apis.gitlabAuthApi),
          ScmAuth.forAzure(apis.microsoftAuthApi),
          ScmAuth.forBitbucket(apis.bitbucketAuthApi),
          // GitHub Enterprise
          ScmAuth.forGithub(apis.githubEnterpriseAuthApi, {
            host: 'git.hub.vwgroup.com',
          }),
          ScmAuth.forGithub(apis.githubEMUAuthApi, {
            host: 'cariad.ghe.com',
          }),
        ),
    }),
});

// Replaces `api:app/storage`: user settings are stored in the backend instead
// of the browser.
export const storageApi = ApiBlueprint.make({
  name: 'storage',
  params: defineParams =>
    defineParams({
      api: storageApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        errorApi: errorApiRef,
        fetchApi: fetchApiRef,
        identityApi: identityApiRef,
      },
      factory: deps => UserSettingsStorage.create(deps),
    }),
});

// Matomo is one implementation behind the core analytics API
// (`api:app/analytics`), which forwards every event to all implementations.
export const matomoAnalytics = AnalyticsImplementationBlueprint.make({
  name: 'matomo',
  params: defineParams =>
    defineParams({
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => MatomoAnalytics.fromConfig(configApi),
    }),
});

export const apisModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    cloudIdpAuthApi,
    microsoftChinaAuthApi,
    githubEnterpriseAuthApi,
    githubEmuAuthApi,
    scmAuthApi,
    storageApi,
    matomoAnalytics,
  ],
});
